import { useMemo } from 'react';
import { LMS_SITE, learningHomeOverride, type LearningHomeOverrideKey } from '@mlm/shared';
import { storefrontAssetUrl } from '../../lib/api';
import { useStorefrontContent } from './hooks';
import { useSiteContent, type SiteContent } from './site-content';

/**
 * The learning pages' editable copy.
 *
 * Two sources feed the same words, and the order matters:
 *
 *  1. the `LMS_SITE` Site Pages overlay (Admin → Learning → Learning Pages) —
 *     wins whenever the admin has actually edited that field;
 *  2. the older typed `storefront.learningHome` block, for the eight fields it
 *     also carries (the hero's title/sub/CTA/photo and the share-and-earn
 *     band's title/body/CTA/link). A tenant that wrote its own copy there
 *     before the manifest existed must keep seeing it;
 *  3. the manifest default.
 *
 * Everything else passes straight through to `useSiteContent`, so a page reads
 * one accessor and never has to know which of the two stores a string came from.
 */

/** Manifest paths that have an older `learningHome` twin. */
const OVERRIDE_PATHS: Record<string, LearningHomeOverrideKey> = {
  'learning.hero.title': 'heroTitle',
  'learning.hero.sub': 'heroSub',
  'learning.hero.ctaLabel': 'heroCta',
  'learning.catalog.heading': 'catalogHeading',
  'learning.earn.title': 'earnTitle',
  'learning.earn.body': 'earnBody',
  'learning.earn.ctaLabel': 'earnCta',
  'learning.earn.ctaHref': 'earnHref',
};

/** The one overlapping IMAGE field — resolved through the same precedence. */
const HERO_IMAGE_PATH = 'learning.hero.image';

export type LearningCopy = SiteContent & {
  /** A section toggle (`show`/`hide`) read as a boolean. */
  shows(path: string): boolean;
};

export function useLearningCopy(): LearningCopy {
  const sc = useSiteContent(LMS_SITE);
  const learningHome = useStorefrontContent().data.learningHome;
  return useMemo(() => {
    const legacy = learningHomeOverride(learningHome);
    const text = (path: string) => {
      if (sc.isCustomized(path)) return sc.text(path);
      const key = OVERRIDE_PATHS[path];
      const fallback = key ? legacy[key] : undefined;
      return fallback ?? sc.text(path);
    };
    return {
      ...sc,
      text,
      img: (path: string) => {
        if (path !== HERO_IMAGE_PATH || sc.isCustomized(path)) return sc.img(path);
        const key = legacy.heroImageKey;
        return key ? storefrontAssetUrl(key) : sc.img(path);
      },
      shows: (path: string) => text(path) !== 'hide',
    };
  }, [sc, learningHome]);
}
